import axios from "axios";
import { appendAuthInterceptor } from "../../../Common/serviceFactory";

const RESEARCH_AUTHORITIES_FILES_BASE_URL = "ResearchAuthorities";

const axiosResearchAuthoritiesInstance = axios.create({
  baseURL: `${process.env.REACT_APP_REQUEST_RESEARCH_AUTORITIES}`,
});

const axiosFilesInstance = axios.create({
  baseURL: `${process.env.REACT_APP_REQUEST_RESEARCH_AUTORITIES_FILES}`,
});

const axiosPublicInstance = axios.create({
  baseURL: process.env.REACT_APP_REQUEST_PUBLIC,
});

appendAuthInterceptor(axiosResearchAuthoritiesInstance);
appendAuthInterceptor(axiosFilesInstance);

const requestWrapper = async (request) => {
  try {
    const res = await request;
    return res.data;
  } catch (error) {
    throw error;
  }
};

const service = {
  async GetProgramTypes() {
    return await requestWrapper(axiosPublicInstance.get(`utility/programs`));
  },
  async getInstitution() {
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(`/contact/institution`)
    );
  },
  async getProposalData(proposalId) {
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(
        `/institution/proposal/${proposalId}`
      )
    );
  },
  async GetInstitutionApplicants(props) {
    const { institutionId } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(
        `/institution/${institutionId}/applicants`
      )
    );
  },
  async GetApplicantsGrantees(props) {
    const { institutionId, grantStatusList } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/grantees/applicants/${institutionId}`,
        JSON.stringify(grantStatusList),
        { headers: { "Content-Type": "application/json" } }
      )
    );
  },
  async GetApplicants(props) {
    const { applicantsIds } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/applicants`,
        JSON.stringify(applicantsIds),
        { headers: { "Content-Type": "application/json" } }
      )
    );
  },
  async GetProposals(props) {
    const { proposalsIds } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/proposals`,
        JSON.stringify(proposalsIds),
        { headers: { "Content-Type": "application/json" } }
      )
    );
  },

  async downloadFiles(props) {
    const { fullPath } = props;
    const filePath = `files/${fullPath}`;
    return await requestWrapper(
      axiosFilesInstance.get(filePath, {
        params: {
          cacheBustTimestamp: Date.now(), // prevents IE cache problems on re-download
        },
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      })
    );
  },

  async getResearchAuthoritiesFiles(props) {
    const { folderName } = props;
    return await requestWrapper(
      axiosFilesInstance.get(
        `files/?path=${RESEARCH_AUTHORITIES_FILES_BASE_URL}/${folderName}`
      )
    );
  },

  async createProposalPDF({ proposalId }) {
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(
        `/proposal/${proposalId}/GeneratePDF`,
        {
          params: {
            idType: "ProposalId",
            header: "",
          },
        }
      )
    );
  },
  async downloadRawFile({ path, filename }) {
    const response = await requestWrapper(
      axiosFilesInstance.get(`files/${path}`, {
        params: {
          cacheBustTimestamp: Date.now(), // prevents IE cache problems on re-download
        },
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      })
    );

    if (!(response instanceof Blob)) {
      return;
    }

    const blob = new Blob([response], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  },

  async UpdateAplicantStatus(props) {
    const { applicantId, status } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/applicant/${applicantId}/updateStatus/${status}`
      )
    );
  },
  async UpdateProposalStatus(props) {
    const { proposalId, status } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/proposal/${proposalId}/updateStatus/${status}`
      )
    );
  },
  async UpdateApplicantRAnote(props) {
    const { applicantId, note } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/applicant/ranote/${applicantId}`,
        note
      )
    );
  },
  async UpdateProposalRAnote(props) {
    const { proposalId, note } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/proposal/ranote/${proposalId}`,
        note
      )
    );
  },
  async UpdateGrantRAnote(props) {
    const { grantId, note } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.post(
        `/institution/grant/ranote/${grantId}`,
        note
      )
    );
  },
  async getSubmitStatus(props) {
    const { proposalId } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(
        `/proposal/${proposalId}/checksubmit`
      )
    );
  },
  async getProposalsApplicants(props) {
    const { proposalId } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(`/proposal/${proposalId}/applicants`)
    );
  },
  async downloadGranteeStatus(props) {
    const { applicantId } = props;
    return await requestWrapper(
      axiosResearchAuthoritiesInstance.get(`grant/${applicantId}/GeneratePDF`, {
        params: {
          cacheBustTimestamp: Date.now(), // prevents IE cache problems on re-download
        },
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      })
    );
  },
};

export default service;
